import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TimeContext = createContext(null);
const IDLE_MS = 5 * 60 * 1000;
const NUDGE_MS = 20 * 60 * 1000; // remind to clock out after 20 min idle
const HEARTBEAT_MS = 30000;

const normPath = (p) =>
  (p || "/")
    .replace(/\/[0-9a-f]{8}-[0-9a-f-]{20,}/gi, "/:id")
    .replace(/\/\d+/g, "/:id");

export function TimeProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [card, setCard] = useState(null);
  const [today, setToday] = useState(null);
  const [recent, setRecent] = useState([]);
  const [now, setNow] = useState(Date.now());
  const lastActivity = useRef(Date.now());
  const lastBeat = useRef(Date.now());
  const nudgedRef = useRef(false);
  const pageRef = useRef(normPath(location.pathname));

  useEffect(() => { pageRef.current = normPath(location.pathname); }, [location.pathname]);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/time/me");
      setCard(data.open || null);
      setToday(data.today || null);
      setRecent(data.recent || []);
    } catch (e) { console.error("time refresh", e); }
  }, []);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  useEffect(() => {
    const mark = () => { lastActivity.current = Date.now(); };
    const evs = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    evs.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, mark));
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const sendBeat = useCallback((beacon = false) => {
    if (!card) return;
    const nowMs = Date.now();
    let delta = Math.round((nowMs - lastBeat.current) / 1000);
    if (delta <= 0) return;
    delta = Math.min(delta, 120);
    const idle = document.hidden || (nowMs - lastActivity.current > IDLE_MS);
    const body = { status: idle ? "idle" : "active", delta_seconds: delta, page: pageRef.current };
    lastBeat.current = nowMs;
    if (beacon && navigator.sendBeacon) {
      navigator.sendBeacon(`${process.env.REACT_APP_BACKEND_URL}/api/time/heartbeat`,
        new Blob([JSON.stringify(body)], { type: "application/json" }));
    } else {
      api.post("/time/heartbeat", body).catch(() => {});
    }
  }, [card]);

  useEffect(() => {
    if (!card) return;
    const t = setInterval(() => sendBeat(false), HEARTBEAT_MS);
    const onHide = () => { if (document.hidden) sendBeat(true); };
    const onUnload = () => sendBeat(true);
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [card, sendBeat]);

  const clockIn = useCallback(async () => {
    try {
      const { data } = await api.post("/time/clock-in");
      setCard(data);
      lastBeat.current = Date.now();
      lastActivity.current = Date.now();
      refresh();
      return true;
    } catch { return false; }
  }, [refresh]);

  const clockOut = useCallback(async () => {
    if (card) sendBeat(false);
    try { await api.post("/time/clock-out"); } catch (e) { console.error("clock-out", e); }
    setCard(null);
    nudgedRef.current = false;
    refresh();
  }, [card, sendBeat, refresh]);

  // Idle nudge: if clocked in and idle for a long stretch, remind the user to clock out
  // so they don't accrue a "no sign-out" shift.
  useEffect(() => {
    if (!card) { nudgedRef.current = false; return; }
    const idleFor = now - lastActivity.current;
    if (idleFor < IDLE_MS) { nudgedRef.current = false; return; }
    if (idleFor > NUDGE_MS && !nudgedRef.current && !document.hidden) {
      nudgedRef.current = true;
      toast.warning("Still clocked in?", {
        duration: 20000,
        description: "You've been idle for a while. Clock out if you're done so your timecard stays accurate.",
        action: { label: "Clock out", onClick: () => clockOut() },
      });
    }
  }, [now, card, clockOut]);

  const elapsed = card ? Math.max(0, Math.floor((now - new Date(card.clock_in).getTime()) / 1000)) : 0;
  const idleNow = card ? (document.hidden || (now - lastActivity.current > IDLE_MS)) : false;

  return (
    <TimeContext.Provider value={{ card, today, recent, elapsed, idleNow, clockedIn: !!card, clockIn, clockOut, refresh }}>
      {children}
    </TimeContext.Provider>
  );
}

export const useTime = () => useContext(TimeContext) || {};
